import React, { useState } from 'react';
import { ApiService } from '../services/api';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateCreated: () => void;
  userEmail?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onTemplateCreated,
  userEmail
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    highlights: '',
    isAvailable: false,
    icon: '🏗️',
    sortOrder: 1
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      // Format highlights as HTML list items
      const highlightsArray = formData.highlights.split('\n').filter(h => h.trim());
      const highlightsHtml = highlightsArray.map(highlight => `<li>${highlight.trim()}</li>`).join('');

      const templateData = {
        ...formData,
        highlights: highlightsHtml
      };

      const result = await ApiService.createArchTemplate(templateData, userEmail);

      if (result.success) {
        setMessage(result.message);
        setMessageType('success');
        // Reset form
        setFormData({
          title: '',
          description: '',
          highlights: '',
          isAvailable: false,
          icon: '🏗️',
          sortOrder: 1
        });
        // Notify parent to refresh templates
        onTemplateCreated();
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
          setMessage('');
        }, 2000);
      } else {
        setMessage(result.message);
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to create template. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let processedValue: string | number | boolean = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Architecture Template</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="create-template-form">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="e.g., Microservices with .NET"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              placeholder="Brief description of the template..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="highlights">Highlights (one per line) *</label>
            <textarea
              id="highlights"
              name="highlights"
              value={formData.highlights}
              onChange={handleInputChange}
              required
              rows={5}
              placeholder="Production-ready microservices architecture&#10;Docker containerization included&#10;API Gateway implementation&#10;Database per service pattern"
            />
            <small className="form-help">Enter each highlight on a new line</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="icon">Icon</label>
              <input
                type="text"
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                placeholder="🏗️"
                maxLength={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="sortOrder">Sort Order</label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                min="1"
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleInputChange}
                />
                <span className="checkbox-custom"></span>
                Available for download
              </label>
            </div>
          </div>

          {message && (
            <div className={`form-message ${messageType}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplateModal;